import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Form, Button, Container, Row, Col, Alert, Spinner, InputGroup, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { EyeFill, EyeSlashFill } from 'react-bootstrap-icons'
import { amazonAPI } from '../api/amazonAPI'
import { brandAPI } from '../api/brandAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { usePermissions } from '../contexts/PermissionContext'

function AmazonAccountUpdateScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const { id } = useParams()
    const navigate = useNavigate()
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [testResult, setTestResult] = useState(null)
    const [testing, setTesting] = useState(false)
    const [availableBrands, setAvailableBrands] = useState([])
    const [allAccounts, setAllAccounts] = useState([])
    const [account, setAccount] = useState({
        account_name: '',
        brand_ids: [],
        is_active: true,
        has_lwa_client_id: false,
        has_lwa_client_secret: false,
        has_refresh_token: false,
    })
    const [credentials, setCredentials] = useState({
        lwa_client_id: '',
        lwa_client_secret: '',
        refresh_token: '',
    })
    const [showSecrets, setShowSecrets] = useState({
        lwa_client_id: false,
        lwa_client_secret: false,
        refresh_token: false,
    })

    useEffect(() => {
        async function fetchData() {
            try {
                const [brandsRes, accounts] = await Promise.all([
                    brandAPI.getAll(1, 1000, '', false),
                    amazonAPI.accounts.getAll(),
                ])
                setAvailableBrands(brandsRes.results || [])
                setAllAccounts(accounts || [])
            } catch (error) {
                console.error('Error fetching brands/accounts:', error)
            }
        }
        fetchData()
    }, [])

    useEffect(() => {
        async function fetchAccount() {
            try {
                const data = await amazonAPI.accounts.getById(id)
                setAccount(data)
            } catch (error) {
                console.error('Error fetching account:', error)
                setError('Failed to load Amazon account')
            } finally {
                setLoading(false)
            }
        }
        fetchAccount()
    }, [id])

    const brandToAccount = {}
    allAccounts.forEach(acc => {
        if (String(acc.id) !== String(id)) {
            (acc.brand_ids || []).forEach(brandId => {
                brandToAccount[brandId] = acc.account_name
            })
        }
    })

    const toggleBrand = (brandId) => {
        const current = account.brand_ids || []
        if (current.includes(brandId)) {
            setAccount({ ...account, brand_ids: current.filter(bid => bid !== brandId) })
        } else {
            setAccount({ ...account, brand_ids: [...current, brandId] })
        }
    }

    const submitHandler = async (e) => {
        e.preventDefault()
        setSubmitting(true)
        try {
            const updateData = {
                account_name: account.account_name,
                brand_ids: account.brand_ids || [],
                is_active: account.is_active,
            }
            if (credentials.lwa_client_id) updateData.lwa_client_id = credentials.lwa_client_id
            if (credentials.lwa_client_secret) updateData.lwa_client_secret = credentials.lwa_client_secret
            if (credentials.refresh_token) updateData.refresh_token = credentials.refresh_token

            await amazonAPI.accounts.update(id, updateData)
            navigate('/amazon-accounts')
        } catch (error) {
            setError(formatErrorResponse(error))
        } finally {
            setSubmitting(false)
        }
    }

    const handleTestConnection = async () => {
        setTesting(true)
        setTestResult(null)
        try {
            const result = await amazonAPI.accounts.testConnection(id)
            setTestResult({ success: true, message: result.message || 'Connection successful' })
        } catch (error) {
            setTestResult({ success: false, message: error.response?.data?.error || 'Connection failed' })
        } finally {
            setTesting(false)
        }
    }

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this Amazon account? This action cannot be undone.')) {
            return
        }
        try {
            await amazonAPI.accounts.delete(id)
            navigate('/amazon-accounts')
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    const handleToggleActive = async () => {
        if (account.is_active) {
            if (!window.confirm('Are you sure you want to deactivate this account?')) {
                return
            }
        }
        try {
            const updateData = {
                account_name: account.account_name,
                is_active: !account.is_active,
            }
            const updated = await amazonAPI.accounts.update(id, updateData)
            setAccount(updated)
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    if (permissionsLoading || loading) {
        return (
            <Container className="py-3">
                <div className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            </Container>
        )
    }

    if (!hasPermission('amazon.manage')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to manage Amazon accounts.
                </Alert>
            </Container>
        )
    }

    return (
        <Container>
            <Row className="justify-content-left">
                <Col style={{ maxWidth: '730px' }}>
                    <h1>Update Amazon Account</h1>
                    <Form onSubmit={submitHandler}>
                        <Form.Group className="mb-4" controlId="account_name">
                            <Form.Label>Account Name*</Form.Label>
                            <Form.Control
                                type="text"
                                value={account.account_name}
                                onChange={(e) => setAccount({ ...account, account_name: e.target.value })}
                                required
                                placeholder="Enter account name"
                                disabled={!account.is_active}
                                style={{ opacity: account.is_active ? 1 : 0.6 }}
                                autoComplete="one-time-code"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="brands">
                            <Form.Label>Brands</Form.Label>
                            <div
                                className="d-flex flex-wrap gap-2 p-3 border rounded"
                                style={{ opacity: account.is_active ? 1 : 0.6 }}
                            >
                                {availableBrands.map((brand) => {
                                    const assignedTo = brandToAccount[brand._id]
                                    const isSelected = (account.brand_ids || []).includes(brand._id)
                                    if (assignedTo) {
                                        return (
                                            <OverlayTrigger
                                                key={brand._id}
                                                overlay={<Tooltip>Assigned to {assignedTo}</Tooltip>}
                                            >
                                                <Badge bg="secondary" style={{ cursor: 'not-allowed', opacity: 0.5 }}>
                                                    {brand.name}
                                                </Badge>
                                            </OverlayTrigger>
                                        )
                                    }
                                    return (
                                        <Badge
                                            key={brand._id}
                                            bg={isSelected ? 'success' : 'light'}
                                            style={{ cursor: account.is_active ? 'pointer' : 'default' }}
                                            onClick={() => account.is_active && toggleBrand(brand._id)}
                                        >
                                            {brand.name}
                                        </Badge>
                                    )
                                })}
                            </div>
                            <Form.Text className="text-muted">
                                Select brands sold on this Amazon account
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="lwa_client_id">
                            <Form.Label>
                                LWA Client ID
                                {account.has_lwa_client_id && (
                                    <small className="text-success ms-2">(configured)</small>
                                )}
                            </Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showSecrets.lwa_client_id ? "text" : "password"}
                                    value={credentials.lwa_client_id}
                                    onChange={(e) => setCredentials({ ...credentials, lwa_client_id: e.target.value })}
                                    placeholder="Enter new value to replace"
                                    disabled={!account.is_active}
                                    style={{ opacity: account.is_active ? 1 : 0.6 }}
                                    autoComplete="one-time-code"
                                />
                                <Button
                                    variant="outline-secondary"
                                    tabIndex={-1}
                                    onClick={() => setShowSecrets({ ...showSecrets, lwa_client_id: !showSecrets.lwa_client_id })}
                                >
                                    {showSecrets.lwa_client_id ? <EyeSlashFill /> : <EyeFill />}
                                </Button>
                            </InputGroup>
                            <Form.Text className="text-muted">
                                Leave blank to keep current value
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="lwa_client_secret">
                            <Form.Label>
                                LWA Client Secret
                                {account.has_lwa_client_secret && (
                                    <small className="text-success ms-2">(configured)</small>
                                )}
                            </Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showSecrets.lwa_client_secret ? "text" : "password"}
                                    value={credentials.lwa_client_secret}
                                    onChange={(e) => setCredentials({ ...credentials, lwa_client_secret: e.target.value })}
                                    placeholder="Enter new value to replace"
                                    disabled={!account.is_active}
                                    style={{ opacity: account.is_active ? 1 : 0.6 }}
                                    autoComplete="one-time-code"
                                />
                                <Button
                                    variant="outline-secondary"
                                    tabIndex={-1}
                                    onClick={() => setShowSecrets({ ...showSecrets, lwa_client_secret: !showSecrets.lwa_client_secret })}
                                >
                                    {showSecrets.lwa_client_secret ? <EyeSlashFill /> : <EyeFill />}
                                </Button>
                            </InputGroup>
                            <Form.Text className="text-muted">
                                Leave blank to keep current value
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="refresh_token">
                            <Form.Label>
                                Refresh Token
                                {account.has_refresh_token && (
                                    <small className="text-success ms-2">(configured)</small>
                                )}
                            </Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showSecrets.refresh_token ? "text" : "password"}
                                    value={credentials.refresh_token}
                                    onChange={(e) => setCredentials({ ...credentials, refresh_token: e.target.value })}
                                    placeholder="Enter new value to replace"
                                    disabled={!account.is_active}
                                    style={{ opacity: account.is_active ? 1 : 0.6 }}
                                    autoComplete="one-time-code"
                                />
                                <Button
                                    variant="outline-secondary"
                                    tabIndex={-1}
                                    onClick={() => setShowSecrets({ ...showSecrets, refresh_token: !showSecrets.refresh_token })}
                                >
                                    {showSecrets.refresh_token ? <EyeSlashFill /> : <EyeFill />}
                                </Button>
                            </InputGroup>
                            <Form.Text className="text-muted">
                                Leave blank to keep current value
                            </Form.Text>
                        </Form.Group>

                        {testResult && (
                            <Alert
                                variant={testResult.success ? 'success' : 'danger'}
                                onClose={() => setTestResult(null)}
                                dismissible
                            >
                                {testResult.message}
                            </Alert>
                        )}

                        {error && (
                            <Alert variant="danger" onClose={() => setError(null)} dismissible>
                                {error}
                            </Alert>
                        )}

                        {!account.is_active && (
                            <Alert variant="warning" className="mb-3">
                                This account is currently inactive. Activate it to make changes.
                            </Alert>
                        )}

                        <Row className="mt-3">
                            <Col>
                                {account.is_active ? (
                                    <>
                                        <Button variant="primary" type="submit" className="me-2" disabled={submitting}>
                                            {submitting ? 'Updating...' : 'Update'}
                                        </Button>
                                        <Button
                                            variant="info"
                                            onClick={handleTestConnection}
                                            className="me-2"
                                            disabled={testing}
                                        >
                                            {testing ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-1" />
                                                    Testing...
                                                </>
                                            ) : (
                                                'Test Connection'
                                            )}
                                        </Button>
                                        <Button
                                            variant="danger"
                                            onClick={handleToggleActive}
                                            className="me-2"
                                        >
                                            Deactivate
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            onClick={handleDelete}
                                            className="me-2"
                                        >
                                            Delete
                                        </Button>
                                        <Button
                                            variant="outline-primary"
                                            onClick={() => navigate('/amazon-accounts')}
                                        >
                                            <i className="fas fa-arrow-left me-2"></i>
                                            Back to List
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            variant="success"
                                            onClick={handleToggleActive}
                                            className="me-2"
                                        >
                                            Activate
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            onClick={handleDelete}
                                            className="me-2"
                                        >
                                            Delete
                                        </Button>
                                        <Button
                                            variant="outline-primary"
                                            onClick={() => navigate('/amazon-accounts')}
                                        >
                                            <i className="fas fa-arrow-left me-2"></i>
                                            Back to List
                                        </Button>
                                    </>
                                )}
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>
        </Container>
    )
}

export default AmazonAccountUpdateScreen
